import React, { useEffect, useMemo, useState } from 'react';

import { Table, type TableSort } from 'src/components/TableNew';
import { SearchInput } from 'src/components/SearchInput';
import Badge from 'src/components/Table/Row/Badge';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { UserTransaction, fetchPlatformUserTransactions } from 'src/api/adminUsers';
import { convertDate, shortenString } from 'src/components/Table/helpers';
import { useSearchQueryValue } from 'src/hooks/useDebouncedSearch';

import {
  EmptyState,
  SearchControl,
  SectionControls,
  SectionTitle,
  SectionToolbar,
  TabPanel,
} from '../styled';

type TransactionsTabProps = {
  userId: string;
  onForbidden?: () => void;
  /** Address to pre-filter by, e.g. when arriving from the Wallets tab. */
  initialSearch?: string;
};

const typeLabel = (tx: UserTransaction) => {
  const kind = String(tx.kind || tx.type || '').toUpperCase();
  const ccy = tx.currency_code || '';
  const prefix = ccy ? `${ccy} - ` : '';
  let base: string;
  switch (kind) {
    case 'WITHDRAW':
    case 'MANUAL_WITHDRAW':
      base = `${prefix}Withdrawal`;
      break;
    case 'DEPOSIT':
    case 'MANUAL_DEPOSIT':
      base = `${prefix}Deposit`;
      break;
    case 'TRANSFER':
      base = `${prefix}Transfer`;
      break;
    default:
      base = String(tx.type || tx.kind || '—');
  }
  const direction = String(tx.direction || '').toUpperCase();
  if (direction === 'IN') return `↓ ${base}`;
  if (direction === 'OUT') return `↑ ${base}`;
  return base;
};

// Columns the API can order by, mapped from the table's field name.
const SORT_FIELDS: Record<string, string> = {
  type_label: 'type',
  status: 'status',
  ccy: 'currency_code',
  amount: 'amount',
  created_at: 'created_at',
};

const TransactionsTab: React.FC<TransactionsTabProps> = ({
  userId,
  onForbidden,
  initialSearch = '',
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<UserTransaction[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sort, setSort] = useState<TableSort>(null);
  const [search, setSearch] = useState(initialSearch);
  const debouncedSearch = useSearchQueryValue(search);

  // Arriving from the Wallets tab for a different wallet re-applies the filter.
  useEffect(() => {
    if (initialSearch) setSearch(initialSearch);
  }, [initialSearch]);
  const field = 'address' as const;

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sort]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const res = await fetchPlatformUserTransactions(userId, {
          page: currentPage,
          limit: 10,
          field: debouncedSearch ? field : undefined,
          value: debouncedSearch || undefined,
          sort: sort ? SORT_FIELDS[sort.field] : undefined,
          order: sort?.direction,
        });
        if (cancelled) return;
        setTransactions(res.transactions);
        setTotalPages(Math.max(1, res.meta.page_count || 1));
        setTotalCount(res.meta.total_count || res.transactions.length);
      } catch (e: any) {
        if (cancelled) return;
        if (e?.response?.status === 403) {
          onForbidden?.();
          return;
        }
        dispatch(
          setAppAlert({
            message: e?.response?.data?.error ?? 'Failed to load transactions',
            isSuccess: false,
          }),
        );
        setTransactions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [userId, currentPage, debouncedSearch, field, sort, dispatch, onForbidden]);

  const rows = useMemo(
    () =>
      transactions.map((tx) => ({
        ...tx,
        id: tx.id,
        address: tx.address || '—',
        type_label: typeLabel(tx),
        status: tx.status,
        ccy: tx.currency_code || '—',
        amount: tx.amount || '—',
        created_at: tx.created_at,
      })),
    [transactions],
  );

  return (
    <TabPanel>
      <SectionToolbar>
        <SectionTitle>Transactions ({totalCount})</SectionTitle>
        <SectionControls>
          <SearchControl>
            <SearchInput
              inline
              showIcon
              placeholder="Search by address..."
              value={search}
              onChange={setSearch}
            />
          </SearchControl>
        </SectionControls>
      </SectionToolbar>

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={['Address', 'Type', 'Status', 'CCY', 'Amount', 'Created On']}
          items={rows}
          columns="1.2fr 1.2fr 0.9fr 0.6fr 0.9fr 1fr"
          fieldsToShow={['address', 'type_label', 'status', 'ccy', 'amount', 'created_at']}
          sortableFields={Object.keys(SORT_FIELDS)}
          sort={sort}
          onSortChange={setSort}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          renderCell={(item, fieldName) => {
            if (fieldName === 'address' && item.address && item.address !== '—') {
              return <span title={item.address}>{shortenString(item.address)}</span>;
            }
            if (fieldName === 'status') return <Badge value={item.status ?? '-'} />;
            if (fieldName === 'created_at') {
              return <span>{item.created_at ? convertDate(item.created_at).date : '—'}</span>;
            }
            return null;
          }}
        />
      ) : (
        <EmptyState>No transactions for this user.</EmptyState>
      )}
    </TabPanel>
  );
};

export default TransactionsTab;
